// Integration Configuration System
// This file imports and exports all integration configurations

import { s3Config } from "./integrations/s3.js";
import { githubConfig } from "./integrations/github.js";
import { awsConfig } from "./identity-management/aws.js";
// import { gcsConfig } from "./identity-management/gcs.js";
// import { azureConfig as azureBlobConfig } from "./identity-management/azure-blob.js";
// import { azureConfig } from "./identity-management/azure.js";
// import { gcpConfig } from "./identity-management/gcp.js";
// import { bitbucketConfig } from "./identity-management/bitbucket.js";
// import { giteaConfig } from "./identity-management/gitea.js";
// import { huggingfaceConfig } from "./identity-management/huggingface.js";
// import { gitlabConfig } from "./identity-management/gitlab.js";
// import { teamsConfig } from "./identity-management/teams.js";
import { activeDirectoryConfig } from "./identity-management/active-directory.js";
// import { fileExtensionsConfig } from "./identity-management/fileExtensions.js";

export const INTEGRATION_TYPES = {
  s3: s3Config,
  github: githubConfig,
  aws: awsConfig,
  // gcp: gcpConfig,
  // azure: azureConfig,
  // gcs: gcsConfig,
  // "azure-blob": azureBlobConfig,
  // bitbucket: bitbucketConfig,
  // gitea: giteaConfig,
  // huggingface: huggingfaceConfig,
  // gitlab: gitlabConfig,
  // teams: teamsConfig,
  "active-directory": activeDirectoryConfig,
  // fileExtensions: fileExtensionsConfig,
};

// Default number of top items to show when not specified by a card
export const DEFAULT_TOP_ITEMS = 8;

// Helper functions to work with integration configs
export const getIntegrationConfig = (type) => {
  return INTEGRATION_TYPES[type] || null;
};

export const getAllIntegrationTypes = () => {
  return Object.keys(INTEGRATION_TYPES);
};

export const getIntegrationEndpoint = (type, endpointKey, params = {}) => {
  const config = getIntegrationConfig(type);
  if (!config || !config.endpoints[endpointKey]) return null;

  let endpoint = config.endpoints[endpointKey];

  // Handle new endpoint structure with method and filters
  if (typeof endpoint === "object" && endpoint.url) {
    const endpointConfig = { ...endpoint };
    // Replace parameters in URL
    Object.entries(params).forEach(([key, value]) => {
      endpointConfig.url = endpointConfig.url.replace(`{${key}}`, value);
    });
    return endpointConfig;
  }

  // Handle legacy string endpoints
  // Replace parameters in endpoint
  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`{${key}}`, value);
  });

  return endpoint;
};

export const getIntegrationDisplayName = (type) => {
  const config = getIntegrationConfig(type);
  return config ? config.displayName : type;
};

export const isValidIntegrationType = (type) => {
  return type && INTEGRATION_TYPES.hasOwnProperty(type);
};

// Helper functions for new configuration sections

/**
 * Get bulk actions configuration for an integration type
 */
export const getBulkActions = (type) => {
  const config = getIntegrationConfig(type);
  return config?.bulkActions || [];
};

/**
 * Get filter configuration for an integration type
 */
export const getFilterConfig = (type) => {
  const config = getIntegrationConfig(type);
  return (
    config?.filterConfig || {
      serverSideFiltering: false,
      supportedFilters: ["verification", "source"],
      defaultFilter: "all",
    }
  );
};

/**
 * Get filter endpoint for an integration type with parameter replacement
 */
export const getFilterEndpoint = (type, params = {}) => {
  const config = getIntegrationConfig(type);
  const filterConfig = config?.filterConfig;

  if (!filterConfig?.filterEndpoint) {
    return null;
  }

  let endpoint = filterConfig.filterEndpoint;

  // Replace parameters in endpoint
  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`{${key}}`, value);
  });

  return endpoint;
};

/**
 * Get validation requirements for an integration type
 */
export const getValidationConfig = (type) => {
  const config = getIntegrationConfig(type);
  return config?.validation || {};
};

/**
 * Get features configuration for an integration type
 */
export const getFeatures = (type) => {
  const config = getIntegrationConfig(type);
  return (
    config?.features || {
      hasInsights: false,
      hasBlastRadius: false,
      supportsAutoFix: false,
      supportsVaultRemediation: false,
      supportsTicketing: false,
    }
  );
};

/**
 * Check if an integration supports a specific feature
 */
export const supportsFeature = (type, feature) => {
  const features = getFeatures(type);
  return features[feature] || false;
};

/**
 * Get validation requirements for a specific validation type
 */
export const getValidationRequirements = (integrationType, validationType) => {
  const validation = getValidationConfig(integrationType);
  return validation[validationType] || null;
};

/**
 * Get bulk action configuration by action key
 */
export const getBulkActionConfig = (type, actionKey) => {
  const bulkActions = getBulkActions(type);
  return bulkActions.find((action) => action.key === actionKey) || null;
};

/**
 * Get all available cell renderer types from integration configs
 */
export const getCellRendererTypes = () => {
  return [
    "detector_badge",
    "verification_status",
    "truncated_text",
    "file_link",
    "file_path",
    "email",
    "severity_badge",
    "date",
    "status_column",
  ];
};

/**
 * Get endpoint with parameter replacement and additional config
 */
export const getIntegrationEndpointConfig = (
  type,
  endpointKey,
  params = {}
) => {
  const config = getIntegrationConfig(type);
  if (!config) return null;

  // Check endpoints first
  if (config.endpoints && config.endpoints[endpointKey]) {
    return getIntegrationEndpoint(type, endpointKey, params);
  }

  // Check bulk actions for endpoints
  const bulkAction = getBulkActionConfig(type, endpointKey);
  if (bulkAction && bulkAction.endpoint) {
    let endpoint = bulkAction.endpoint;
    Object.entries(params).forEach(([key, value]) => {
      endpoint = endpoint.replace(`{${key}}`, value);
    });

    return {
      url: endpoint,
      method: bulkAction.method || "POST",
      baseUrl: bulkAction.baseUrl || "API_URL",
    };
  }

  return null;
};

/**
 * Get auto-fix polling configuration for an integration type
 */
export const getAutoFixPollingConfig = (type) => {
  const config = getIntegrationConfig(type);
  return (
    config?.autoFixPolling || {
      enabled: false,
      statusEndpoint: "api/v1/remediation/jobs/{job_id}",
      pollInterval: 5000,
      maxPollTime: 600000,
      baseUrl: "AI_URL",
      enableLogging: false,
    }
  );
};